import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Translate } from 'react-localize-redux';
import Icon, { IconSizes } from '../../components/foundations/Icon';
import MarkdownText from '../../components/foundations/MarkdownText';
import css from './Reasoning.css';

const TRANSLATION_PREFIX = 'STATIC.PAGES.ANALYSIS_CHAT.REASONING';
const MS_IN_S = 1000;

const ThinkingLabel = () => (
    <span className={css.thinkingLabel}>
        <Translate id={`${TRANSLATION_PREFIX}.THINKING`} />
    </span>
);

const ThoughtForLabel = ({ duration }) => {
    if (duration === undefined || duration === null) {
        return <Translate id={`${TRANSLATION_PREFIX}.THOUGHT_FOR_A_FEW_SECONDS`} />;
    }
    const key = duration === 1 ? 'THOUGHT_FOR_SECOND' : 'THOUGHT_FOR_SECONDS';
    return <Translate id={`${TRANSLATION_PREFIX}.${key}`} data={{ count: duration }} />;
};

ThoughtForLabel.propTypes = { duration: PropTypes.number };

const Reasoning = ({ reasoning, isMessageActive }) => {
    // A reasoning summary may arrive in multiple chunks across one turn (one
    // per LLM call, with tool calls in between). Between chunks no entry has
    // isStreaming=true even though the agent is still working — so we also
    // treat the message-level active state as "thinking" to avoid prematurely
    // showing "Thought for N seconds".
    const hasStreamingChunk = reasoning.some(r => r.isStreaming);
    const isStreaming = hasStreamingChunk || isMessageActive;
    const combined = reasoning.map(r => r.text || '').filter(Boolean).join('\n\n');

    // Always collapsed by default. User clicks to expand/collapse — no auto-open
    // during streaming and no auto-close after.
    const [isOpen, setIsOpen] = useState(false);
    const [duration, setDuration] = useState(null);
    const startTimeRef = useRef(null);

    useEffect(() => {
        if (isStreaming) {
            if (startTimeRef.current === null) startTimeRef.current = Date.now();
        } else if (startTimeRef.current !== null) {
            setDuration(Math.max(1, Math.ceil((Date.now() - startTimeRef.current) / MS_IN_S)));
            startTimeRef.current = null;
        }
    }, [isStreaming]);

    if (!reasoning.length || !combined) return null;

    return (
        <div className={css.reasoning}>
            <button
                type="button"
                className={css.trigger}
                onClick={() => setIsOpen(prev => !prev)}
                aria-expanded={isOpen}
            >
                <Icon
                    name="brain"
                    size={IconSizes.X_SMALL}
                    className={css.brainIcon}
                />
                {isStreaming ? <ThinkingLabel /> : <ThoughtForLabel duration={duration} />}
                <Icon
                    name="chevron-down"
                    size={IconSizes.X_SMALL}
                    className={classNames(css.chevron, { [css.chevronOpen]: isOpen })}
                />
            </button>
            {isOpen && (
                <div className={css.content}>
                    <MarkdownText content={combined} />
                </div>
            )}
        </div>
    );
};

Reasoning.propTypes = {
    reasoning: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            text: PropTypes.string,
            isStreaming: PropTypes.bool,
        })
    ).isRequired,
    isMessageActive: PropTypes.bool,
};

Reasoning.defaultProps = {
    isMessageActive: false,
};

export default Reasoning;
